import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useIsSchoolStaff, schoolLinkLabel } from "@/components/school/SchoolHub";
import { schoolApi } from "@/lib/school";
import { HelpCircle, Play, LogIn } from "lucide-react";
import { LogOut, CalendarDays, Map, Settings, Home, School, Users, Heart, Lightbulb, Menu, X } from "lucide-react";

export default function Nav() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  // School principals and teachers also get a "School" page (a read-only view of their school, shared by every app).
  const { staff: isSchoolStaff, role: schoolRole } = useIsSchoolStaff(schoolApi, !!user);
  // Individual (family) accounts: "Back to home" (the dashboard, still signed in) replaces Sign out (platform admins too). School roles keep Sign out.
  const isIndividual = !!user;   // everyone signed in gets "Back to home" in place of Sign out
  // Below md, the links/register-school/login-signup row has nowhere left to
  // shrink to and previously just wrapped into ragged, disconnected lines
  // (confirmed live at 390px). A hamburger collapses everything but the
  // brand mark into one slide-down panel instead, matching the sibling
  // Little Verses app's mobile header pattern.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const links = user ? [
    // Reading level varies hugely across ages 8-13, so an icon
    // carries the meaning and the label confirms it — never label-only.
    { to: "/dashboard/today", label: t("today"), icon: CalendarDays },
    { to: "/community", label: "Ideas", icon: Lightbulb },
    // One school entry per person: school staff get the shared "My School" / "My Students" page, so the
    // older Principal / Classes links are hidden for them (they remain for accounts without a gateway school).
    ...(user.role === "admin" || user.role === "teacher"
      ? [{ to: "/teacher", label: "Classes", icon: Users },
         ...(user.role === "admin" ? [{ to: "/admin", label: t("admin"), icon: Settings }] : [])]
      : []),
    ...(user.role === "principal"
      ? [{ to: "/principal", label: "My School", icon: School }]
      : []),
    ...(isSchoolStaff && user.role !== "principal" ? [{ to: "/school", label: schoolLinkLabel(schoolRole), icon: School }] : []),
  ] : [
    // Signed-out visitors get the persona fork in the nav too, so a principal
    // arriving on a shared link is one tap from the school path.
    { to: "/families", label: "For Families", icon: Home },
    { to: "/schools", label: "For Schools", icon: School },
  ];
  const publicLinks = [
    { href: "/#weekly", label: "Stories" },
    { href: "/#how-it-works", label: "How it works" },
    { href: "/#faq", label: "FAQ" },
    { href: "/#promise", label: "Promise" },
  ];
  const pill = "inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-2 font-bold text-hv-navy hover:bg-hv-cream text-sm min-h-[44px] shrink-0 whitespace-nowrap";

  // "A" from "Aabid" / "aabid@..." — matches the sibling Little Verses app's
  // colored-initial avatar instead of a generic icon.
  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();
  // Landing page shows just a Dashboard pill for signed-in users (matching
  // the sibling app's landing header); every other page shows the full set.
  const onLanding = location.pathname === "/" || location.pathname === "/families";
  const visibleLinks = onLanding
    ? [{ to: "/dashboard", label: "Dashboard", icon: Map }]
    : links;

  return (
    <>
    <header className="w-full bg-hv-cream/95 backdrop-blur border-b border-[#E5DFC8] sticky top-0 z-40">
      {/* Plain flex-wrap, no forced-nowrap breakpoint. This used to force
          nowrap once the viewport passed 1240px (min-[1240px]:flex-nowrap),
          back when the container capped at max-w-7xl (1280px) and a wide
          viewport meant a wide container. Now that the container caps at
          max-w-6xl (1152px, matching the sibling Little Verses app), the
          container's available width no longer grows with the viewport —
          it's stuck at ~1152px minus padding regardless of how wide the
          window gets. Confirmed live: with the old nowrap class still in
          place, the full signed-in row (nav links + register-school +
          language + user pill + logout) no longer fit in that fixed width
          at ANY viewport past 1240px, and forcing nowrap anyway crushed
          the brand text ("FROM CRADLE TO GRAVE" clipped) and made the
          right-hand cluster wrap internally on top of that — a visibly
          broken header, not just tight. Plain wrap lets the row split
          cleanly into two lines instead (nav links on one, the
          register/language/user/logout cluster on the next, right-aligned
          by ms-auto within its own wrapped line) — confirmed clean at
          1152, 1240, 1400 and 1600px. */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex flex-wrap items-center gap-x-2 gap-y-3">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5">
          <span className="bg-white rounded-xl p-1.5 shadow-sm shrink-0">
            <img src="/brand/hikmahverses-mark.png" alt="HikmahVerses" className="h-9 w-auto object-contain" />
          </span>
          <div className="leading-tight">
            <div className="hv-heading text-hv-navy text-xl">HikmahVerses</div>
            <div className="text-xs uppercase tracking-[0.15em] text-hv-teal font-bold whitespace-nowrap">From Cradle to Grave</div>
          </div>
        </Link>

        {onLanding && (
          <nav className="hidden lg:flex items-center gap-5 text-sm font-bold text-hv-navy ms-4" data-testid="public-nav-links">
            {publicLinks.map((l) => (
              <a key={l.href} href={l.href} className="hover:text-hv-coral transition">{l.label}</a>
            ))}
          </nav>
        )}
        <nav className="hidden">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.replace("/", "")}-link`}
                className={`px-2.5 min-h-[44px] rounded-full text-sm font-bold transition inline-flex items-center gap-1.5 ${
                  location.pathname === l.to
                    ? "bg-hv-navy text-white"
                    : "text-hv-navy hover:bg-white"
                }`}
              >
                {Icon && <Icon size={16} strokeWidth={2.5} />}
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="ms-auto hidden md:flex flex-nowrap items-center gap-1.5">
          {user ? (
            <>
              {visibleLinks.map((l) => {
                const Icon = l.icon;
                const active = location.pathname === l.to;
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-${l.to.replace("/", "")}-link`}
                    className={`px-2.5 min-h-[44px] rounded-full text-sm font-bold transition inline-flex items-center gap-1.5 shrink-0 ${
                      active ? "bg-hv-navy text-white" : "text-hv-navy hover:bg-white"
                    }`}
                  >
                    {Icon && <Icon size={16} strokeWidth={2.5} />}
                    {l.label}
                  </Link>
                );
              })}
              {!onLanding && <div className="hidden lg:flex items-center gap-2 bg-hv-tealTint rounded-full ps-1.5 pe-3 py-1.5">
                <span className="w-6 h-6 rounded-full bg-hv-teal text-white text-xs font-bold flex items-center justify-center shrink-0">
                  {initial}
                </span>
                <span className="text-sm font-bold text-hv-navy" data-testid="user-name">{user.name || user.email}</span>
              </div>}
              {/* De-emphasized relative to the pill buttons around it, same
                  hierarchy as the sibling app's plain "Sign out" text link —
                  it's a way out, not an action to draw the eye to. */}
              <button
                onClick={() => { logout(); }}
                data-testid={isIndividual ? "home-btn" : "logout-btn"}
                className="min-h-[44px] px-3 rounded-full text-sm font-bold text-hv-slate hover:text-hv-navy hover:bg-white inline-flex items-center gap-1.5 shrink-0"
              >
                {isIndividual ? <><Home size={16} /> Back to home</> : <><LogOut size={16} /> {t("logout")}</>}
              </button>
            </>
          ) : (
            <>
              <Link to="/story/1" data-testid="nav-play-first" className="hv-btn-primary hidden lg:inline-flex py-2 px-3.5 text-sm whitespace-nowrap"><Play size={14} fill="white" strokeWidth={0} /> Play the first story</Link>
              <Link to="/login" data-testid="nav-login-link" className={pill}><LogIn size={16} strokeWidth={2.4} /> Sign in</Link>
            </>
          )}
        </div>

        {/* Mobile-only hamburger — everything in the row above is hidden
            below md, so this is the only way to reach it on a phone. */}
        <button
          type="button"
          onClick={() => setMobileNavOpen((v) => !v)}
          className="ms-auto md:hidden rounded-full p-2.5 text-hv-navy hover:bg-white min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileNavOpen}
          data-testid="nav-mobile-toggle"
        >
          {mobileNavOpen ? <X size={20} strokeWidth={2.5} /> : <Menu size={20} strokeWidth={2.5} />}
        </button>
      </div>

      {mobileNavOpen && (
        <div className="md:hidden border-t border-[#E5DFC8] bg-hv-cream px-5 pb-4 pt-2" data-testid="nav-mobile-panel">
          {!user && (
            <nav className="flex flex-col text-sm font-bold text-hv-navy" data-testid="mobile-public-links">
              {publicLinks.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setMobileNavOpen(false)} className="py-3 border-b border-[#E5DFC8]/70 last:border-0">{l.label}</a>
              ))}
            </nav>
          )}
          {user && links.length > 0 && (
            <nav className="flex flex-col text-sm font-bold text-hv-navy">
              {links.map((l) => {
                const Icon = l.icon;
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setMobileNavOpen(false)}
                    className="py-3 border-b border-[#E5DFC8]/70 last:border-0 inline-flex items-center gap-2"
                  >
                    {Icon && <Icon size={16} strokeWidth={2.5} />}
                    {l.label}
                  </Link>
                );
              })}
            </nav>
          )}
          <div className="mt-3 flex flex-col gap-2">
            {user ? (
              <>
                <div className="flex items-center gap-2 bg-hv-tealTint rounded-full px-3 py-2 justify-center">
                  <span className="w-6 h-6 rounded-full bg-hv-teal text-white text-xs font-bold flex items-center justify-center shrink-0">
                    {initial}
                  </span>
                  <span className="text-sm font-bold text-hv-navy" data-testid="user-name-mobile">{user.name || user.email}</span>
                </div>
                <button
                  onClick={() => { setMobileNavOpen(false); logout(); }}
                  data-testid={isIndividual ? "home-btn-mobile" : "logout-btn-mobile"}
                  className="flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-bold text-hv-slate hover:text-hv-navy"
                >
                  {isIndividual ? <><Home size={16} /> Back to home</> : <><LogOut size={16} /> {t("logout")}</>}
                </button>
              </>
            ) : (
              <>
                <Link to="/story/1" onClick={() => setMobileNavOpen(false)} data-testid="mobile-play-first" className="hv-btn-primary py-2.5 px-4 text-sm text-center">Play the first story</Link>
                <Link to="/login" onClick={() => setMobileNavOpen(false)} data-testid="nav-login-link-mobile" className="hv-btn-secondary py-2.5 px-4 text-sm text-center">Sign in</Link>
                <Link to="/signup" onClick={() => setMobileNavOpen(false)} data-testid="nav-signup-link-mobile" className="py-2.5 px-4 text-sm text-center font-bold text-hv-navy">Register</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
    </>
  );
}
